import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../styles/panel/user/TicketArchive.css";
import { useAuth } from "../../../context/AuthContext";
import { getUserTickets } from "../../../api/support";

const FILTERS = [
  { id: "open", label: "باز" },
  { id: "closed", label: "بسته" },
];

function formatRelativeTime(dateString) {
  if (!dateString) return "—";
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "همین الان";
  if (m < 60) return `${m} دقیقه پیش`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ساعت پیش`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} روز پیش`;
  return new Date(dateString).toLocaleDateString("fa-IR");
}
const STATUS_LABEL = {
  open: "باز",
  closed: "بسته",
};

const PAGE_SIZE = 20;

export default function TicketArchive() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("open");
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUserTickets({
        status: activeTab === "open" ? undefined : activeTab,
        page,
      });
      const list = res.data.results ?? res.data;
      setTickets(list);
      setTotalCount(res.data.count ?? list.length);
      setTotalPages(
        res.data.count ? Math.max(1, Math.ceil(res.data.count / PAGE_SIZE)) : 1,
      );
    } catch {
      setError("خطا در دریافت تیکت‌ها.");
      setTickets([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  // با تغییر فیلتر وضعیت، صفحه باید به ۱ برگردد
  const handleFilterChange = (id) => {
    if (id === activeTab) return;
    setActiveTab(id);
    setPage(1);
  };

  const handleRowClick = (id) => {
    navigate(`${id}`);
  };

  const pageNumbers = useMemo(() => {
    const pages = [];
    for (let p = 1; p <= totalPages; p++) pages.push(p);
    return pages;
  }, [totalPages]);

  return (
    <div className="archive-page" dir="rtl">
      <div className="archive-header">
        <div>
          <div className="archive-title">تیکت های من</div>
          <div className="archive-subtitle">
            مشاهده و پیگیری تیکت‌های ارسال‌شده به پشتیبانی
          </div>
        </div>

        <div className="status-filter">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`filter-btn${activeTab === f.id ? " active" : ""}`}
              onClick={() => handleFilterChange(f.id)}
            >
              {f.label}
              {f.id === activeTab && (
                <span className="filter-count">{totalCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="archive-card">
        {loading ? (
          <div className="archive-empty">در حال بارگذاری...</div>
        ) : error ? (
          <div className="archive-empty">{error}</div>
        ) : tickets.length === 0 ? (
          <div className="archive-empty">تیکتی با این وضعیت یافت نشد.</div>
        ) : (
          <div className="ticket-table-wrap">
            <table className="ticket-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>عنوان</th>
                  <th>آخرین پیام از</th>
                  <th>نام فروشگاه</th>
                  <th>وضعیت</th>
                  <th>آخرین به‌روزرسانی</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t, i) => (
                  <tr
                    key={t.id}
                    className="ticket-row"
                    onClick={() => handleRowClick(t.id)}
                  >
                    <td className="cell-id">{i + 1}</td>
                    <td className="cell-title">
                      <span className="cell-title-link">{t.title}</span>
                    </td>
                    <td className="cell-sender">{t.last_sender.name}</td>
                    <td className="cell-sender">{t.store}</td>

                    <td>
                      <span className={`status-badge ${t.status}`}>
                        {/* <span className="status-dot" /> */}
                        {STATUS_LABEL[t.status] ?? t.status}
                      </span>
                    </td>
                    <td className="cell-date">
                      {formatRelativeTime(t.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            قبلی
          </button>

          <div className="page-numbers">
            {pageNumbers.map((p) => (
              <button
                key={p}
                className={`page-number${p === page ? " active" : ""}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            className="page-btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            بعدی
          </button>
        </div>
      )}
    </div>
  );
}
